// The two tables that answer "what have I logged at this place" -- every
// Captain's / Pilot's entry and every trip leg whose route reaches it.
//
// Extracted from PlaceVisits so the EDIT page can show the same answer below
// its map without growing a second implementation. Two callers, one difference:
//
//   /me/places/:id/visits  draws the routes on a map above, so a row can be
//                          flagged as drawn and the totals can own up to what
//                          isn't. showOnMap (the default) is that page.
//   /me/places/:id         has only the place's own pin. OnMapFlag's tooltip is
//                          literally "Drawn on the map above", so showing it
//                          there would assert something false -- the column and
//                          the "N not on the map" total both go.
//
// Everything else -- the columns, the empty state, the totals, the navigation
// targets -- is deliberately identical, because a record listed under a place is
// the same record whichever page you reached it from.
//
// TWO DELIBERATE ABSENCES (inherited from PlaceVisits, and still true here):
//
//   No hours or distance TOTAL. A record's hours and miles belong to the whole
//   voyage, not to this place: summing them under a harbour's name would state
//   something false ("402 nm at Roche Harbor" is not a fact about Roche
//   Harbor). Counts and a date range are what this page can honestly add up.
//
//   No truncation. Every record is reachable however long the list gets; the
//   tables start at PLACE_RECORDS_TO_SHOW rows and grow on Show more (the
//   Trips page's control), which is a page, not a cap. Only a MAP is ever
//   capped, and the caller that has one says so via mapNote.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import { DataCard, ShowMore, useShowMore } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import { displayDate } from '../../fleet-shared/Common.mjs';
import {
  urlForLogbookEntry,
  urlForPlaceVisits,
  urlForTrip,
} from '../../fleet-shared/Urls.mjs';
import {
  entryTitle,
  logbookTypeById,
  readEntryField,
  LOGBOOK_TYPE_MAINTENANCE,
} from '../../fleet-shared/LogbookTypes.mjs';
import { displayHours, displayNm } from '../../fleet-shared/LogbookTrips.mjs';
import { tripGroupDateRange } from '../../fleet-shared/TripGroups.mjs';
import { placeCell } from '../../fleet-shared/PlaceFields.mjs';

// Rows each table opens with. A harbour visited three hundred times is three
// hundred rows, and rendering them all at once was what made a well-visited
// place the slowest page in the app; the reader still reaches every one.
export const PLACE_RECORDS_TO_SHOW = 25;

// Where a record opens. Shared so both callers navigate identically -- and note
// what is NOT here: no router state of any kind. listAllItems selects only
// { id, name }, so handing `item` over would give useSharedItemPageState a
// crippled Item that it CONSUMES instead of fetching, and the destination would
// render with no tasks, no counters and no logbooks. Build the navigation from
// the STORED row (`record`), never the resolved display copy: a pre-resolved
// handover would leave the destination's routeGen at 0 and silently skip its
// lazy write-back.
export function placeRecordUrl(row) {
  return row.kind === 'entry'
    ? urlForLogbookEntry(row.itemId, row.logbookId, row.id)
    : urlForTrip(row.itemId, row.id);
}

// One item's read failing must not cost the user the other thirteen, so
// usePlaceRecords collects them instead of erroring. This is where they surface.
export const PlaceRecordFailures = ({ failures }) => {
  const [dismissed, setDismissed] = useState(false);
  if (!failures?.length || dismissed) return null;
  return (
    <Container className="pt-2">
      <Alert variant="warning" dismissible onClose={() => setDismissed(true)} className="mb-0 py-2">
        Couldn&apos;t read {failures.length} of your items, so some trips and entries may be
        missing from this list.
      </Alert>
    </Container>
  );
};

// A record drawn on the map above. One narrow trailing column, the same idiom
// as the logbook table's Notes flag -- and the honest answer for a record that
// reaches this place only through its From/To name and has no route to draw.
const OnMapFlag = ({ on }) =>
  on ? (
    <i
      className="fa-solid fa-location-dot"
      title="Drawn on the map above"
      aria-label="Drawn on the map above"
      role="img"
    ></i>
  ) : null;

// An entry's hours field is the type's, not a column named `hours`: the
// Captain's Log logs `hours` and the Pilot's Logbook `pic`.
function entryHours(entry, typeId) {
  const type = logbookTypeById(typeId);
  const key = type.summary?.hoursKey;
  if (!key) return null;
  const field = type.fields.find((f) => f.key === key);
  if (!field) return null;
  const value = Number(readEntryField(entry, field));
  return Number.isFinite(value) ? value : null;
}

const EntriesTable = ({ rows, placeIndex, onSelect, showOnMap }) => (
  <Table responsive hover className="align-middle mb-0">
    <thead className="bg-body-tertiary">
      <tr>
        <th scope="col">Item</th>
        <th scope="col">Logbook</th>
        <th scope="col">Date</th>
        <th scope="col">Entry</th>
        <th scope="col" className="text-end">Hours</th>
        {showOnMap && <th scope="col" className="text-center visually-hidden">On the map</th>}
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => {
        const typeId = row.logbook?.logbookType || LOGBOOK_TYPE_MAINTENANCE;
        const hours = entryHours(row.display, typeId);
        return (
          <tr key={row.id} onClick={() => onSelect(row)} role="button" style={{ cursor: 'pointer' }}>
            <td className="align-middle entered-data">{row.item?.name}</td>
            <td className="align-middle entered-data">{row.logbook?.name}</td>
            <td className="align-middle entered-data text-nowrap">{displayDate(row.date)}</td>
            {/* The short gazetteer names, the way every one-line summary reads. */}
            <td className="align-middle entered-data">
              {entryTitle(row.display, typeId, { placeIndex })}
            </td>
            <td className="align-middle entered-data text-end">
              {hours == null ? '' : displayHours(hours)}
            </td>
            {showOnMap && (
              <td className="align-middle text-center">
                <OnMapFlag on={row.usage.onMap} />
              </td>
            )}
          </tr>
        );
      })}
    </tbody>
  </Table>
);

const TripsTable = ({ rows, placeIndex, onSelect, showOnMap }) => (
  <Table responsive hover className="align-middle mb-0">
    <thead className="bg-body-tertiary">
      <tr>
        <th scope="col">Item</th>
        <th scope="col">Date</th>
        <th scope="col">Trip</th>
        <th scope="col">From</th>
        <th scope="col">To</th>
        <th scope="col" className="text-end">Distance</th>
        {showOnMap && <th scope="col" className="text-center visually-hidden">On the map</th>}
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => {
        // placeCell shortens only a gazetteer-backed name and keeps the full
        // one on hover; a hand-typed comma is shown as typed.
        const from = placeCell(row.from, placeIndex);
        const to = placeCell(row.to, placeIndex);
        return (
          <tr key={row.id} onClick={() => onSelect(row)} role="button" style={{ cursor: 'pointer' }}>
            <td className="align-middle entered-data">{row.item?.name}</td>
            <td className="align-middle entered-data text-nowrap">{displayDate(row.date)}</td>
            <td className="align-middle entered-data">{row.display?.name || '(unnamed)'}</td>
            <td className="align-middle entered-data" title={from.title}>{from.text}</td>
            <td className="align-middle entered-data" title={to.title}>{to.text}</td>
            <td className="align-middle entered-data text-end">{displayNm(row.display?.distance)}</td>
            {showOnMap && (
              <td className="align-middle text-center">
                <OnMapFlag on={row.usage.onMap} />
              </td>
            )}
          </tr>
        );
      })}
    </tbody>
  </Table>
);

export const PlaceRecordsCard = ({
  place,
  records,
  placeIndex,
  onSelect,
  heading,
  // The caller with a routes map above. See the header for why this is not
  // cosmetic: without one, the flag and the "not on the map" total both name
  // something that isn't on the page.
  showOnMap = true,
  notOnMapCount = 0,
  mapNote,
  extraActions,
}) => {
  const entries = useMemo(() => (records ?? []).filter((r) => r.kind === 'entry'), [records]);
  const trips = useMemo(() => (records ?? []).filter((r) => r.kind === 'trip'), [records]);
  // Before the early return below: hooks run on every render.
  const entriesMore = useShowMore(entries.length, PLACE_RECORDS_TO_SHOW);
  const tripsMore = useShowMore(trips.length, PLACE_RECORDS_TO_SHOW);

  // Counts and a span -- see the header for why there is no hours or nm total.
  const totals = useMemo(() => {
    if (!records) return null;
    const times = records.map((r) => Date.parse(r.date)).filter(Number.isFinite).sort((a, b) => a - b);
    const parts = [];
    if (times.length > 0) {
      const range = tripGroupDateRange({
        startDate: new Date(times[0]).toISOString(),
        endDate: new Date(times[times.length - 1]).toISOString(),
      });
      if (range) parts.push(range);
    }
    if (entries.length > 0) {
      parts.push(`${entries.length} logbook ${entries.length === 1 ? 'entry' : 'entries'}`);
    }
    if (trips.length > 0) parts.push(`${trips.length} ${trips.length === 1 ? 'trip' : 'trips'}`);
    if (showOnMap && notOnMapCount > 0) parts.push(`${notOnMapCount} not on the map`);
    return parts;
  }, [records, entries.length, trips.length, notOnMapCount, showOnMap]);

  if (!records) return null;

  // A failed gazetteer read gives EMPTY maps, indistinguishable from an empty
  // table -- and with no index nothing resolves, so every record silently fails
  // to match. An empty page that means "the network failed" must never read as
  // "nothing was ever logged here".
  const indexFailed = placeIndex != null && placeIndex.byId.size === 0;

  // Two rows can hold one name key, and pickPlace elects exactly one of them to
  // answer for it. If this row LOST that election, every record that names the
  // place without a binding is listed under the winner -- and an empty list
  // here is otherwise baffling, because the records plainly say this name.
  const keyOwner = place && !indexFailed ? placeIndex?.byKey?.get(place.nameKey) : null;
  const nameLostTo = keyOwner && keyOwner.id !== place.id ? keyOwner : null;

  return (
    <DataCard
      header={<CardHeader text={heading} belowText={totals?.join(' · ')} extraActions={extraActions} />}
      body={
        records.length === 0 ? (
          <div className="text-center text-body-secondary py-3">
            {indexFailed ? (
              <p className="mb-0">
                Couldn&apos;t read the place list, so nothing could be matched. Try again.
              </p>
            ) : (
              <>
                <p className="mb-1">Nothing refers to this place yet.</p>
                <p className="small mb-0">
                  A trip leg or logbook entry reaches a place when one of its waypoints is
                  bound to it, or its From or To matches its name.
                </p>
                {nameLostTo && (
                  <p className="small mb-0 mt-2">
                    Another place shares this name and lookups resolve to it, so records
                    that name this place are listed under{' '}
                    <Link to={urlForPlaceVisits(nameLostTo.id)}>{nameLostTo.name}</Link>.
                  </p>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            {mapNote && (
              <p className="text-center text-body-secondary small mb-3" data-testid="map-cap-note">
                The map is {mapNote}; the tables below are complete.
              </p>
            )}

            {entries.length > 0 && (
              <>
                <h6 className="fw-semibold mb-2">Logbook Entries</h6>
                <EntriesTable
                  rows={entries.slice(0, entriesMore.shown)}
                  placeIndex={placeIndex}
                  onSelect={onSelect}
                  showOnMap={showOnMap}
                />
                {entriesMore.canShowMore && <ShowMore onClick={entriesMore.showMore} />}
              </>
            )}

            {trips.length > 0 && (
              <>
                <h6 className={`fw-semibold mb-2 ${entries.length > 0 ? 'mt-4' : ''}`}>Trips</h6>
                <TripsTable
                  rows={trips.slice(0, tripsMore.shown)}
                  placeIndex={placeIndex}
                  onSelect={onSelect}
                  showOnMap={showOnMap}
                />
                {tripsMore.canShowMore && <ShowMore onClick={tripsMore.showMore} />}
              </>
            )}
          </>
        )
      }
    />
  );
};

export default PlaceRecordsCard;
